import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ticketService, statusService, userService } from '../services/api';
import { Select, Button, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { useAuth } from '../context/AuthContext';
import './TicketDetails.css';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const TicketDetails = () => {
  const toast = useToast();
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const chatEndRef = useRef(null);
  const fileInputRef2 = useRef(null);

  // Core state
  const [ticket, setTicket] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [remark, setRemark] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [reassignUserId, setReassignUserId] = useState('');
  const [eligibleUsers, setEligibleUsers] = useState([]);
  // BioMedical assigns from the department's agents rather than from the
  // ticket's tagged users, which is an IT concept BM tickets never have.
  const isBioMedical = (currentUser?.department_code || 'IT') === 'BM';
  const canAssign = !!currentUser?.can_assign_tickets;
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);

  // Action loading states
  const [statusLoading, setStatusLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  // Escalation state
  const [escalationInfo, setEscalationInfo] = useState(null);
  const [escalateLoading, setEscalateLoading] = useState(false);
  const [showEscalateConfirm, setShowEscalateConfirm] = useState(false);
  const [escalateRemarks, setEscalateRemarks] = useState('');

  useEffect(() => {
    fetchStatuses();
    fetchTicket();
    fetchEligibleUsers();
    fetchEscalationInfo();
    markTicketAsRead();
  }, [id]);

  const markTicketAsRead = async () => {
    try { await ticketService.markAsRead(id); } catch (e) { /* silent */ }
  };

  const fetchStatuses = async () => {
    try {
      const response = await statusService.getAll({ page: 1, limit: 1000 });
      setStatuses(response.data.data || response.data);
    } catch (e) { console.error('Error fetching statuses:', e); }
  };

  const fetchEligibleUsers = async () => {
    try {
      const response = await ticketService.getEligibleUsersForAssignment(id);
      setEligibleUsers(response.data.users || []);
    } catch (e) { console.error('Error fetching eligible users:', e); }
  };

  // assignable=true asks the server which roles may hold a ticket, so no role
  // ids live in here — they differ between databases.
  const fetchAssignableUsers = async (ticketData) => {
    if (!isBioMedical || !canAssign) return;
    try {
      // Only agents who cover this ticket's branch are offered.
      const codes = (ticketData?.branches || [])
        .map(b => b.branch_code)
        .filter(Boolean);
      const params = { limit: 500, assignable: 'true' };
      if (codes.length > 0) params.branch_codes = codes.join(',');
      const response = await userService.getAll(params);
      setAssignableUsers((response.data?.data || []).filter(u => u.status !== false));
    } catch (e) { console.error('Error fetching assignable users:', e); }
  };

  const getTimeRemaining = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    if (diff <= 0) return { text: 'Overdue', overdue: true };
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, overdue: false };
    }
    return { text: `${hours}h ${minutes}m`, overdue: false };
  };

  const fetchEscalationInfo = async () => {
    try {
      const response = await ticketService.getEscalationInfo(id);
      setEscalationInfo(response.data);
    } catch (e) { console.error('Error fetching escalation info:', e); }
  };

  const handleEscalate = async () => {
    try {
      setEscalateLoading(true);
      await ticketService.escalateTicket(id, { remarks: escalateRemarks || undefined });
      toast.success('Ticket escalated successfully');
      setShowEscalateConfirm(false);
      setEscalateRemarks('');
      fetchTicket();
      fetchEscalationInfo();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to escalate ticket');
    } finally {
      setEscalateLoading(false);
    }
  };

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await ticketService.getById(id);
      setTicket(response.data);
      setNewStatus(response.data.status);
      setAttachments(response.data.attachments || []);
      // Needs the ticket's branches, so it runs once the ticket is loaded
      // rather than on mount.
      fetchAssignableUsers(response.data);
    } catch (err) {
      setError('Failed to fetch ticket');
    } finally {
      setLoading(false);
    }
  };

  // --- Status change (no comment needed) ---
  const handleStatusChange = async () => {
    if (newStatus === ticket.status) {
      toast.warning('Status is already ' + ticket.status);
      return;
    }
    try {
      setStatusLoading(true);
      const formData = new FormData();
      formData.append('status', newStatus);
      await ticketService.submitAction(id, formData);
      toast.success('Status updated successfully');
      fetchTicket();
      fetchEligibleUsers();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  // --- Reassign to single user ---
  const handleReassignUser = async () => {
    if (!reassignUserId) {
      toast.warning('Please select a user to assign');
      return;
    }
    try {
      setAssignLoading(true);
      const formData = new FormData();
      formData.append('assigned_user_id', reassignUserId);
      await ticketService.submitAction(id, formData);
      toast.success('User reassigned successfully');
      setReassignUserId('');
      fetchTicket();
      fetchEligibleUsers();
    } catch (err) {
      toast.error('Failed to reassign user');
    } finally {
      setAssignLoading(false);
    }
  };

  // --- File validation & handling ---
  const acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip'];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const errors = [];
    const validFiles = [];

    if (newAttachments.length + files.length > 5) {
      errors.push(`Max 5 files allowed. Currently selected: ${newAttachments.length}`);
      setFileErrors(errors);
      e.target.value = '';
      return;
    }

    files.forEach(file => {
      if (newAttachments.some(f => f.name === file.name)) {
        errors.push(`${file.name}: Already selected`);
        return;
      }
      if (!acceptedFormats.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }
      const sizeMB = file.size / (1024 * 1024);
      const isImage = file.type.startsWith('image/');
      if (isImage && sizeMB > 5) {
        errors.push(`${file.name}: Image exceeds 5MB limit`);
        return;
      }
      if (!isImage && sizeMB > 20) {
        errors.push(`${file.name}: File exceeds 20MB limit`);
        return;
      }
      validFiles.push(file);
    });

    setNewAttachments(prev => [...prev, ...validFiles]);
    setFileErrors(errors);
    e.target.value = '';
  };

  const handleRemoveFile = (indexToRemove) => {
    setNewAttachments(prev => prev.filter((_, i) => i !== indexToRemove));
    setFileErrors([]);
  };

  // --- Comment + optional file upload ---
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (newAttachments.length > 0 && !remark.trim()) {
      toast.warning('Comment is required when uploading files');
      return;
    }
    if (!remark.trim() && newAttachments.length === 0) {
      toast.warning('Please write a comment or upload files');
      return;
    }
    try {
      setCommentLoading(true);
      const formData = new FormData();
      if (remark.trim()) formData.append('remark', remark.trim());
      newAttachments.forEach(file => formData.append('attachments', file));
      await ticketService.submitAction(id, formData);
      toast.success(newAttachments.length > 0 ? 'Comment & files added' : 'Comment added');
      setRemark('');
      setNewAttachments([]);
      fetchTicket();
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setCommentLoading(false);
    }
  };

  // Loading / error states
  if (loading && !ticket) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-lg text-gray-500">Loading ticket...</div>
    </div>
  );
  if (error) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-red-600">{error}</div>
    </div>
  );
  if (!ticket) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-gray-600">Ticket not found</div>
    </div>
  );

  // --- Helper functions ---
  const getStatusColor = (statusName) => {
    const status = statuses.find(s => s.name === statusName);
    if (status && status.color) {
      return {
        backgroundColor: status.color,
        color: getContrastColor(status.color),
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '600'
      };
    }
    return null;
  };

  const getContrastColor = (hexColor) => {
    const color = hexColor.replace('#', '');
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#000000' : '#ffffff';
  };

  const getPriorityColor = (priority) => {
    const colors = { 'P3': 'text-green-600', 'P2': 'text-yellow-600', 'P1': 'text-orange-600', 'Critical': 'text-red-600' };
    return colors[priority] || 'text-gray-600';
  };

  const getPriorityLabel = (priority) => {
    const labels = { 'P1': 'High', 'P2': 'Medium', 'P3': 'Low', 'Critical': 'Critical' };
    return labels[priority] || priority;
  };

  // --- Build consolidated activity timeline ---
  const buildTimeline = () => {
    const items = [];

    // Track auto-generated comments we'll replace with history entries
    const statusChangeCommentTimes = new Set();
    const assignmentCommentTexts = new Set();

    // 1. Assignment changes from history
    (ticket.history || []).forEach(h => {
      if (h.field_name === 'assigned_user' && h.action === 'update') {
        items.push({
          type: 'assignment_change',
          data: h,
          timestamp: new Date(h.created_at),
          user: h.user_name || h.actor_name || 'System',
          userId: h.user_id,
          oldValue: h.old_value,
          newValue: h.new_value,
        });
      }
      if (h.field_name === 'status' && h.action === 'update') {
        items.push({
          type: 'status_change',
          data: h,
          timestamp: new Date(h.created_at),
          user: h.user_name || h.actor_name || 'System',
          userId: h.user_id,
          oldValue: h.old_value,
          newValue: h.new_value,
        });
        statusChangeCommentTimes.add(new Date(h.created_at).getTime());
      }
    });

    // 2. Comments — skip auto-generated status change & assignment comments
    (ticket.comments || []).forEach(c => {
      const isAutoStatusChange = c.comment?.startsWith('Status changed from') || c.comment?.startsWith('Status changed to');
      const isAssignmentComment = c.comment?.startsWith('Assigned to') ||
        c.comment?.startsWith('User assigned') ||
        c.comment?.startsWith('Ticket assigned') ||
        c.comment?.match(/^(Reassigned|Unassigned|Tagged|Removed user)/i);
      if (isAutoStatusChange || isAssignmentComment) return;
      items.push({
        type: 'comment',
        data: c,
        timestamp: new Date(c.created_at),
        user: c.user_name || c.actor_name || c.actor_email || 'System',
        userId: c.user_id,
      });
    });

    // 3. Attachments
    (attachments || []).forEach(a => {
      items.push({
        type: 'attachment',
        data: a,
        timestamp: new Date(a.created_at),
        user: a.uploader_name || a.uploaded_by_name || a.uploaded_by_email || 'System',
      });
    });

    items.sort((a, b) => b.timestamp - a.timestamp); // newest first
    return items;
  };

  const timelineItems = buildTimeline();

  // --- Profile icon helper ---
  const ProfileIcon = ({ name, color = 'bg-gray-500' }) => (
    <div className={`w-7 h-7 ${color} text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5`}>
      {name?.charAt(0).toUpperCase() || '?'}
    </div>
  );

  // --- Render individual timeline item ---
  const isCurrentUser = (item) => {
    if (item.userId && currentUser?.user_id) return item.userId === currentUser.user_id;
    if (item.user && currentUser?.name) return item.user === currentUser.name;
    return false;
  };

  const renderTimelineItem = (item, index) => {
    const isMine = isCurrentUser(item);
console.log('Rendering timeline item:', item);
    // --- Status change (escalation-style with status color symbol) ---
    if (item.type === 'status_change') {
      const statusColor = {
        'Open': '#3b82f6', 'In Progress': '#f59e0b', 'Resolved': '#10b981',
        'Closed': '#27ae60', 'Reopen': '#e74c3c', 'Pending': '#8b5cf6'
      };
      const newColor = statusColor[item.newValue] || '#f59e0b';
      return (
        <div key={`status-${index}`} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
          <ProfileIcon name={item.user} color={isMine ? 'bg-gray-600' : 'bg-gray-500'} />
          <div className="max-w-[70%]">
            <div className="rounded-lg px-3 py-2 bg-gray-50 border border-gray-200 rounded-tl-sm">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: newColor }}></span>
                <span>
                  Changed status from <span className="font-semibold">{item.oldValue || '—'}</span> to{' '}
                  <span className="font-semibold" style={{ color: newColor }}>{item.newValue}</span>
                </span>
              </div>
            </div>
            <p className={`text-[10px] text-gray-400 mt-0.5 ${isMine ? 'text-right' : ''} px-1`}>
              {item.user} &middot; {formatDateTimeDDMMYYYY(item.timestamp)}
            </p>
          </div>
        </div>
      );
    }

    // --- Assignment change ---
    if (item.type === 'assignment_change') {
      return (
        <div key={`assign-${index}`} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
          <ProfileIcon name={item.user} color={isMine ? 'bg-green-600' : 'bg-green-500'} />
          <div className="max-w-[70%]">
            <div className={`rounded-lg px-3 py-2 ${isMine ? 'bg-green-600 text-white rounded-tr-sm' : 'bg-green-50 border border-green-200 text-green-800 rounded-tl-sm'}`}>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-green-500"></span>
                <span>
                  <span className="font-semibold">{item.user}</span> reassigned the ticket from{' '}
                  <span className="font-semibold">{item.oldValue || 'Unassigned'}</span> to{' '}
                  <span className="font-semibold">{item.newValue}</span>
                </span>
              </div>
            </div>
            <p className={`text-[10px] text-gray-400 mt-0.5 ${isMine ? 'text-right' : ''} px-1`}>
              {item.user} &middot; {formatDateTimeDDMMYYYY(item.timestamp)}
            </p>
          </div>
        </div>
      );
    }

    // --- Attachment ---
    if (item.type === 'attachment') {
      const att = item.data;
      return (
        <div key={`att-${index}`} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
          <ProfileIcon name={item.user} color={isMine ? 'bg-violet-600' : 'bg-violet-500'} />
          <div className="max-w-[70%]">
            <div className={`rounded-lg px-3 py-2 ${isMine ? 'bg-violet-600 text-white' : 'bg-violet-50 border border-violet-200 text-violet-900'}`}>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span className="text-sm font-medium truncate">{att.filename}</span>
                <span className={`text-xs ${isMine ? 'text-white/70' : 'text-violet-400'}`}>{(att.filesize / 1024).toFixed(1)} KB</span>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${att.filepath.startsWith('/') ? '' : '/'}${att.filepath}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-xs font-medium mt-1 inline-block ${isMine ? 'text-white/80 hover:text-white' : 'text-violet-600 hover:text-violet-800'}`}
              >
                Download
              </a>
            </div>
            <p className={`text-[10px] text-gray-400 mt-0.5 ${isMine ? 'text-right' : ''} px-1`}>
              {item.user} &middot; {formatDateTimeDDMMYYYY(item.timestamp)}
            </p>
          </div>
        </div>
      );
    }

    // --- Comment (chat bubble) ---
    return (
      <div key={`comment-${index}`} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
        <ProfileIcon name={item.user} color={isMine ? 'bg-primary-600' : 'bg-gray-500'} />
        <div className="max-w-[70%]">
          <div className={`rounded-lg px-3 py-2 ${isMine ? 'bg-primary-600 text-white rounded-tr-sm' : 'bg-gray-100 text-gray-800 rounded-tl-sm'}`}>
            <p className="text-sm whitespace-pre-wrap">{item.data.comment}</p>
          </div>
          <p className={`text-[10px] text-gray-400 mt-0.5 ${isMine ? 'text-right' : ''} px-1`}>
            {item.user} &middot; {formatDateTimeDDMMYYYY(item.timestamp)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="ticket-details-container min-h-screen bg-gray-50 ">
      {/* <div className="max-w-7xl mx-auto"> */}
        <div className="max-w-100rem mx-auto">
        {/* Compact Header */}
        <div className="sticky top-0 z-40 bg-gray-50 pb-2">
          {/* <div className="pt-3 px-4 lg:px-6"> */}
             <div className="pt-3">
            <Breadcrumbs
              items={[
                { label: 'Tickets', to: '/tickets' },
                { label: `Ticket #${id}` }
              ]}
            />
          </div>
          {/* <div className="px-4 lg:px-6 mt-2"> */}
          <div className=" mt-1">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-lg px-5 py-3 text-white flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium bg-white/20 px-2.5 py-0.5 rounded-full">
                {ticket.ticket_id || `#${ticket.id}`}
              </span>
              {/* Status with inline edit using Select component */}
              {editingStatus ? (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <div className="w-36 text-gray-900">
                    <Select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="text-xs py-0.5"
                      searchable={false}
                      showClearButton={false}
                    >
                      {statuses.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </Select>
                  </div>
                  <button
                    onClick={async () => { await handleStatusChange(); setEditingStatus(false); }}
                    disabled={statusLoading || newStatus === ticket.status}
                    className="text-[10px] bg-white text-primary-700 px-2 py-1 rounded font-semibold hover:bg-white/90 disabled:opacity-50"
                  >
                    {statusLoading ? '...' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setNewStatus(ticket.status); setEditingStatus(false); }}
                    className="text-[10px] bg-white/20 text-white px-2 py-1 rounded hover:bg-white/30"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 cursor-pointer group" onClick={() => setEditingStatus(true)}>
                  {(() => {
                    const style = getStatusColor(ticket.status);
                    return style ? (
                      <span style={{...style, fontSize: '0.65rem', padding: '0.15rem 0.5rem'}}>{ticket.status}</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/20">{ticket.status}</span>
                    );
                  })()}
                  <svg className="w-3 h-3 text-white/60 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              )}
              {ticket.priority && (
                <span className={`text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority} ({getPriorityLabel(ticket.priority)})
                </span>
              )}
              <h1 className="text-lg font-bold flex-1 truncate ml-1">{ticket.subject}</h1>
              {ticket.resolved_by && ticket.resolved_at && (
                <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">
                  &#10003; Resolved by {ticket.resolver_name || 'Unknown'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        {/* <div className="px-4 lg:px-6 pb-4 mt-2"> */}
                  <div className=" pb-4 mt-2">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:h-[calc(100vh-160px)]">

            {/* ═══ LEFT COLUMN ═══ */}
            <div className="lg:overflow-y-auto pr-1 left-scroll-section space-y-3">

              {/* Ticket Information */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-2 pb-1.5 border-b flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Ticket Information
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-1.5">
                  {[
                    { label: 'Created By', value: ticket.system_created ? 'System' : (ticket.creator_name || '-') },
                    { label: 'Sender', value: ticket.system_created ? (ticket.sender_name || ticket.sender_email || '-') : (ticket.creator_name || '-'), tooltip: ticket.system_created ? (ticket.sender_email || null) : (ticket.creator_email || null) },
                    { label: 'Created At', value: formatDateTimeDDMMYYYY(ticket.created_at) },
                    { label: 'Last Updated', value: formatDateTimeDDMMYYYY(ticket.updated_at) },
                    { label: 'Category', value: ticket.category_name || '-' },
                    // BioMedical raises tickets against a device. These are the
                    // values captured when the ticket was created, so they stay
                    // correct even if the equipment record changes later.
                    ...(ticket.equipment_id ? [
                      { label: 'Equipment', value: ticket.equipment_name || '-', tooltip: ticket.equipment_code || null },
                      { label: 'Manufacturer', value: ticket.manufacturer || '-' },
                      { label: 'Model', value: ticket.model || '-' },
                      { label: 'Serial Number', value: ticket.serial_number || '-' },
                    ] : []),
                  ].map((item, i) => (
                    <div key={i} className="min-w-0">
                      <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">{item.label}</label>
                      <p
                        className={`text-[11px] text-gray-900 leading-tight truncate ${item.tooltip ? 'cursor-help underline decoration-dotted decoration-gray-400' : ''}`}
                        title={item.tooltip ? `${item.value} — ${item.tooltip}` : item.value}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                  {/* Display multiple regions */}
                  <div className="min-w-0">
                    <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">{isBioMedical ? 'Entities' : 'Region(s)'}</label>
                    <p className="text-[11px] text-gray-900 leading-tight" title={ticket.regions && ticket.regions.length > 0 ? ticket.regions.map(r => r.region_name).join(', ') : '-'}>
                      {ticket.regions && ticket.regions.length > 0
                        ? ticket.regions.map(r => r.region_name).join(', ')
                        : '-'}
                    </p>
                  </div>
                  {/* Display multiple branches */}
                  <div className="min-w-0 col-span-2">
                    <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Branch(es)</label>
                    <p className="text-[11px] text-gray-900 leading-tight" title={ticket.branches && ticket.branches.length > 0 ? ticket.branches.map(b => b.branch_name).join(', ') : '-'}>
                      {ticket.branches && ticket.branches.length > 0
                        ? ticket.branches.map(b => b.branch_name).join(', ')
                        : '-'}
                    </p>
                  </div>
                  {ticket.resolved_by && ticket.resolved_at && (
                    <>
                      <div className="min-w-0">
                        <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Resolved By</label>
                        <p className="text-[11px] text-gray-900 leading-tight truncate">{ticket.resolver_name || 'Unknown'}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Resolved At</label>
                        <p className="text-[11px] text-gray-900 leading-tight truncate">{formatDateTimeDDMMYYYY(ticket.resolved_at)}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Total Time Taken</label>
                        <p className="text-[11px] text-gray-900 leading-tight truncate">
                          {(() => {
                            const created = new Date(ticket.created_at);
                            const resolved = new Date(ticket.resolved_at);
                            const diffMs = resolved - created;
                            const totalMins = Math.floor(diffMs / 60000);
                            const days = Math.floor(totalMins / 1440);
                            const hours = Math.floor((totalMins % 1440) / 60);
                            const mins = totalMins % 60;
                            return [days > 0 && `${days}d`, hours > 0 && `${hours}h`, `${mins}m`].filter(Boolean).join(' ');
                          })()}
                        </p>
                      </div>
                    </>
                  )}
                  {ticket.resolution_due && (
                    <div className="min-w-0">
                      <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Need to Resolve At</label>
                      <p className="text-[11px] text-gray-900 leading-tight truncate">{formatDateTimeDDMMYYYY(ticket.resolution_due)}</p>
                    </div>
                  )}
                </div>
                {ticket.description && (
                  <div className="mt-2 pt-2 border-t">
                    <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Description</label>
                    <p className="text-[11px] text-gray-700 whitespace-pre-wrap mt-0.5 max-h-32 overflow-y-auto">{ticket.description}</p>
                  </div>
                )}
              </div>

              {/* Assignment Section */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-2 pb-1.5 border-b flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Assignment
                </h3>

                {/* Assigned user with Change button */}
                <div className="mb-2">
                  <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Assigned To</label>
                  {ticket.assigned_user ? (
                    <div className="flex items-center gap-2 p-1.5 rounded-lg bg-green-50 mt-1">
                      <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {ticket.assigned_user.user_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-gray-900">{ticket.assigned_user.user_name}</p>
                        <p className="text-[9px] text-gray-400">{ticket.assigned_user.user_id}</p>
                      </div>
                      {((ticket.tagged_users && ticket.tagged_users.length > 0) ||
                        (isBioMedical && canAssign)) && (
                        <button
                          onClick={() => { setShowReassign(!showReassign); setReassignUserId(''); }}
                          className="text-[10px] text-primary-600 hover:text-primary-800 font-medium px-2 py-0.5 rounded border border-primary-200 hover:bg-primary-50 transition-colors"
                        >
                          {showReassign ? 'Cancel' : 'Change'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-[11px] text-gray-400 italic flex-1">Unassigned</p>
                      {((ticket.tagged_users && ticket.tagged_users.length > 0) ||
                        (isBioMedical && canAssign)) && (
                        <button
                          onClick={() => { setShowReassign(!showReassign); setReassignUserId(''); }}
                          className="text-[10px] text-primary-600 hover:text-primary-800 font-medium px-2 py-0.5 rounded border border-primary-200 hover:bg-primary-50 transition-colors"
                        >
                          {showReassign ? 'Cancel' : 'Assign'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Reassign dropdown - shows when Change is clicked */}
                {showReassign && (
                  <div className="mb-2 p-2 rounded-lg bg-blue-50 border border-blue-100">
                    <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                      {isBioMedical
                        ? (ticket.assigned_user ? 'Reassign to agent' : 'Assign an agent')
                        : (ticket.assigned_user ? 'Reassign to tagged user' : 'Assign user')}
                    </label>
                    <div className="flex items-center gap-2">
                      <Select
                        value={reassignUserId}
                        onChange={(e) => setReassignUserId(e.target.value)}
                        className="flex-1 text-xs"
                        searchable={false}
                        showClearButton={false}
                      >
                        <option value="">Select user...</option>
                        {/* BioMedical picks from the department's agents; IT
                            keeps reassigning among the ticket's tagged users. */}
                        {(isBioMedical
                          ? assignableUsers.map(u => ({ user_id: u.user_id, user_name: u.name || u.email }))
                          : (ticket.tagged_users || []))
                          .filter(u => u.user_id !== ticket.assigned_user?.user_id)
                          .map(u => (
                            // One string, not several children: Select reads
                            // child.props.children as the label, and an array
                            // of children stringifies with commas between them.
                            <option key={u.user_id} value={u.user_id}>
                              {u.user_name === u.user_id ? u.user_name : `${u.user_name} (${u.user_id})`}
                            </option>
                          ))}
                      </Select>
                      <Button
                        onClick={async () => { await handleReassignUser(); setShowReassign(false); }}
                        variant="primary"
                        disabled={assignLoading || !reassignUserId}
                        className="whitespace-nowrap text-[10px] px-2 py-1"
                      >
                        {assignLoading ? '...' : 'Assign'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tagged users - exclude the assigned user */}
                {ticket.tagged_users && ticket.tagged_users.filter(u => u.user_id !== ticket.assigned_user?.user_id).length > 0 && (
                  <div>
                    <label className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Tagged Users</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {ticket.tagged_users
                        .filter(u => u.user_id !== ticket.assigned_user?.user_id)
                        .map((user, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px] text-gray-700">
                          <span className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0">
                            {user.user_name?.charAt(0).toUpperCase()}
                          </span>
                          {user.user_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Escalation / SLA Section */}
              {escalationInfo?.hasEscalation && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-xs font-semibold text-gray-900 mb-2 pb-1.5 border-b flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Escalation &amp; SLA
                    <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${
                      escalationInfo.currentLevel >= 2 ? 'bg-red-100 text-red-700' :
                      escalationInfo.currentLevel === 1 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {escalationInfo.currentLevel === 0 ? '-' : `L${escalationInfo.currentLevel}`}
                    </span>
                  </h3>

                  {/* Escalation Level & Cumulative Contacts */}
                  {escalationInfo.levels && escalationInfo.levels.length > 0 && escalationInfo.currentLevel > 0 && (
                    <div className="mb-3">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Escalation Contacts</label>
                      <div className="flex flex-wrap gap-1">
                        {escalationInfo.levels
                          .filter(level => level.levelNumber <= escalationInfo.currentLevel)
                          .flatMap(level => [
                            ...level.contacts.users.map((u, idx) => (
                              <span key={`u-${level.levelNumber}-${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-xs text-gray-700 border">
                                <span className="w-4 h-4 bg-orange-500 text-white rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0">
                                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                                {u.name}
                                <span className="text-[9px] text-gray-400">L{level.levelNumber}</span>
                              </span>
                            )),
                            ...level.contacts.otherContacts.map((c, idx) => (
                              <span key={`o-${level.levelNumber}-${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-xs text-gray-500 border border-dashed" title={c.email}>
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {c.name}
                                <span className="text-[9px] text-gray-400">L{level.levelNumber}</span>
                              </span>
                            ))
                          ])}
                      </div>
                    </div>
                  )}

                  {/* Escalation History */}
                  {escalationInfo.escalationHistory && escalationInfo.escalationHistory.length > 0 && (
                    <div className="mb-3">
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Escalation History</label>
                      <div className="space-y-1">
                        {escalationInfo.escalationHistory.map((h, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                            <span className="text-gray-400">{formatDateTimeDDMMYYYY(h.timestamp)}</span>
                            <span>L{h.fromLevel} → L{h.toLevel}</span>
                            <span className="text-gray-400">{h.escalatedBy}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ═══ RIGHT COLUMN - Conversation ═══ */}
            <div className="lg:overflow-y-auto right-scroll-section">

              {/* Sticky Comment + File Upload at top */}
              <div className="sticky top-0 z-10 bg-white rounded-lg shadow p-3 mb-3">
                <form onSubmit={handleCommentSubmit}>
                  {/* Single-line: Input + File Icon + Send */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      ref={fileInputRef2}
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xlsx,.zip"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef2.current?.click()}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                      title="Attach files"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={commentLoading || (!remark.trim() && newAttachments.length === 0)}
                      className="text-xs px-3 py-1.5 whitespace-nowrap"
                    >
                      {commentLoading ? '...' : (
                        <svg className="w-4 h-4 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      )}
                    </Button>
                  </div>
                  {/* File validation info - small text like username/date display */}
                  <p className="text-[10px] text-gray-400 mt-1 px-1">
                    jpg, png, gif, pdf, doc, docx, xlsx, zip &middot; Images max 5MB &middot; Files max 20MB &middot; Max 5 files
                  </p>
                  {/* Uploaded files list */}
                  {newAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5 px-1">
                      {newAttachments.map((file, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-[10px] text-primary-700 border border-primary-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="truncate max-w-[120px]">{file.name}</span>
                          <span className="text-primary-400">({(file.size / 1024).toFixed(0)}KB)</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="text-primary-400 hover:text-red-500 ml-0.5"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* File validation errors */}
                  {fileErrors.length > 0 && (
                    <div className="mt-1 px-1">
                      {fileErrors.map((err, i) => (
                        <p key={i} className="text-[10px] text-red-500">{err}</p>
                      ))}
                    </div>
                  )}
                  {newAttachments.length > 0 && !remark.trim() && (
                    <p className="text-[10px] text-amber-600 mt-1 px-1">* Comment is required when uploading files</p>
                  )}
                </form>
              </div>

              {/* Chat Messages */}
              <div className="space-y-1 px-1">
                {timelineItems.length > 0 ? (
                  <>
                    {timelineItems.map((item, index) => renderTimelineItem(item, index))}
                    <div ref={chatEndRef} />
                  </>
                ) : (
                  <p className="text-gray-400 italic text-center py-8 text-sm">No messages yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
